'use client'

import type { DetailedProposal } from '@moment/horizon-data-access/proposal'
import type { BondType } from '@moment/trading-types/reference-data'
import type { AccountDetail, Lot } from '@/types/api/accounts'

import { useMemo } from 'react'

import { BodySubtext, BodyXS, FontWeight } from '@/components/atomic/text'
import { AccountInvestmentIncomeBarChart } from '@/components/molecules/account-investment-income-bar-chart'
import { Distributions } from '@/components/molecules/distributions'
import { StatCard, StatCardWithDelta } from '@/components/molecules/stat-card'
import {
  getRatingDisplay,
  useGetRatingsAccess,
} from '@/hooks/use-rating-columns'
import { useCashflowData } from '@/portfolio/hooks/use-cashflow-data'
import { CashflowIncomeChart } from '@/portfolio/proposals/cashflow-target/cashflow-income-chart'
import { LoadingAnimationType } from '@/shared/constants/animations'
import { OrderSide } from '@/types/enums/order'
import { formatYield } from '@/utils/presentation/bonds'
import {
  formatMoney,
  formatNumber,
  formatPercentage,
} from '@/utils/presentation/formatting'

import { AccountSummaryStats } from './styles'

interface SummarySectionProps {
  selectedSleeve?: string | null
  accountDetails?: AccountDetail
  isLoadingAccountDetails?: boolean
  showSummaryChartAndCallouts?: boolean
  colors?: {
    fixedIncome: string
    cash: string
    other: string
  }
  showDistributionHeader?: boolean
  chartContainerClassName?: string
  distributionGridClassName?: string
  // When provided along with showDeltas=true, show differences vs this comparison
  comparisonAccountDetails?: AccountDetail
  showDeltas?: boolean
  // Proposal data for showing buy/sell orders
  proposal?: DetailedProposal
  selectedAccount?: string
  // Whether to include the buy/sell order tiles (orders are proposal-level data)
  showOrderStats?: boolean
}

export const SummarySection = ({
  accountDetails,
  selectedSleeve,
  isLoadingAccountDetails = false,
  showDistributionHeader = false,
  chartContainerClassName,
  distributionGridClassName,
  comparisonAccountDetails,
  showDeltas = false,
  proposal,
  selectedAccount,
  showOrderStats = true,
}: SummarySectionProps) => {
  const ratingsAccess = useGetRatingsAccess()

  const assetClasses = useMemo(() => {
    if (!accountDetails) {
      return new Set<BondType>()
    }
    return accountDetails.lots
      .filter((lot: Lot) => {
        if (selectedSleeve) {
          return lot.sleeve_id === selectedSleeve
        }
        return true
      })
      .map((lot: Lot) => {
        return lot.instrument.type
      })
      .reduce((acc: Set<BondType>, assetClass: BondType) => {
        acc.add(assetClass)
        return acc
      }, new Set<BondType>())
  }, [accountDetails, selectedSleeve])

  const distributionData = useMemo(() => {
    if (!accountDetails) {
      return {
        maturity_distribution: {},
        sector_distribution: {},
        sp_rating_distribution: {},
        moodys_rating_distribution: {},
        issuer_distribution: {},
        coupon_distribution: {},
        municipal_state_distribution: {},
        municipal_type_distribution: {},
      }
    }

    const sleeveStats = selectedSleeve
      ? // biome-ignore lint/style/noNonNullAssertion: suppress noUncheckedIndexedAccess
        accountDetails.sleeves[selectedSleeve]!.statistics
      : accountDetails.statistics

    return {
      maturity_distribution: sleeveStats.maturity_distribution,
      sector_distribution: sleeveStats.sector_distribution,
      sp_rating_distribution: sleeveStats.sp_rating_distribution,
      moodys_rating_distribution: sleeveStats.moodys_rating_distribution,
      issuer_distribution: sleeveStats.issuer_distribution,
      coupon_distribution: sleeveStats.coupon_distribution,
      municipal_state_distribution: sleeveStats.municipal_state_distribution,
      municipal_type_distribution: sleeveStats.municipal_type_distribution,
    }
  }, [accountDetails, selectedSleeve])

  const totalForDistribution = useMemo(() => {
    return selectedSleeve
      ? // biome-ignore lint/suspicious/noNonNullAssertedOptionalChain: suppress noUncheckedIndexedAccess
        // biome-ignore lint/style/noNonNullAssertion: suppress noUncheckedIndexedAccess
        (accountDetails?.sleeves[selectedSleeve]!.statistics
          .total_market_value ?? 0)
      : (accountDetails?.statistics.total_market_value ?? 0)
  }, [accountDetails, selectedSleeve])

  // Calculate buy/sell order statistics from proposal data
  const orderStats = useMemo(() => {
    if (!showOrderStats || !proposal || !selectedAccount) {
      return {
        buyOrders: 0,
        buyOrdersCount: 0,
        sellOrders: 0,
        sellOrdersCount: 0,
      }
    }

    // Get the account's allocations to filter orders
    const accountAllocations =
      proposal.accounts[selectedAccount]?.allocations || []
    const relevantAllocationIds = new Set(
      accountAllocations.map((alloc) => alloc.proposal_allocation_id),
    )

    if (!proposal.orders || proposal.orders.length === 0) {
      return {
        buyOrders: 0,
        buyOrdersCount: 0,
        sellOrders: 0,
        sellOrdersCount: 0,
      }
    }

    let buyOrders = 0
    let buyOrdersCount = 0
    let sellOrders = 0
    let sellOrdersCount = 0

    // Process each order
    proposal.orders.forEach((order) => {
      // Filter allocations for this account/sleeve
      const relevantAllocations = order.allocations.filter((allocation) => {
        // Check if this allocation belongs to the selected account
        if (allocation.moment_account_id !== selectedAccount) return false
        // Check if this allocation is in the account's allocation list
        if (!relevantAllocationIds.has(allocation.proposal_allocation_id))
          return false
        // If a sleeve is selected, filter by sleeve
        if (selectedSleeve && allocation.sleeve_id !== selectedSleeve)
          return false
        return true
      })

      // If there are no relevant allocations for this order, skip it
      if (relevantAllocations.length === 0) return

      // Sum up the market values for this order
      const orderMarketValue = relevantAllocations.reduce(
        (sum, allocation) => sum + allocation.market_value,
        0,
      )

      // Add to buy or sell totals
      if (order.side === OrderSide.Buy) {
        buyOrders += orderMarketValue
        buyOrdersCount += 1
      } else if (order.side === OrderSide.Sell) {
        sellOrders += orderMarketValue
        sellOrdersCount += 1
      }
    })

    return { buyOrders, buyOrdersCount, sellOrders, sellOrdersCount }
  }, [showOrderStats, proposal, selectedAccount, selectedSleeve])

  const summaryStats: Array<{
    label: string
    value: string | React.ReactNode
    delta?: number
    deltaFormatter?: (v: number) => string
  }> = useMemo(() => {
    if (!accountDetails) return []
    const avgYtw = selectedSleeve
      ? // biome-ignore lint/style/noNonNullAssertion: suppress noUncheckedIndexedAccess
        accountDetails.sleeves[selectedSleeve]!.statistics.average_ytw
      : accountDetails.statistics.average_ytw
    const avgPurchaseYtw = selectedSleeve
      ? // biome-ignore lint/style/noNonNullAssertion: suppress noUncheckedIndexedAccess
        accountDetails.sleeves[selectedSleeve]!.statistics.average_purchase_ytw
      : accountDetails.statistics.average_purchase_ytw
    const avgMaturity = selectedSleeve
      ? // biome-ignore lint/style/noNonNullAssertion: Bulk suppression as part of biome migration
        accountDetails!.sleeves[selectedSleeve]!.statistics.average_maturity
      : accountDetails.statistics.average_maturity
    const avgDuration = selectedSleeve
      ? // biome-ignore lint/style/noNonNullAssertion: Bulk suppression as part of biome migration
        accountDetails!.sleeves[selectedSleeve]!.statistics.average_duration
      : accountDetails.statistics.average_duration
    const avgSpRating = selectedSleeve
      ? // biome-ignore lint/style/noNonNullAssertion: Bulk suppression as part of biome migration
        accountDetails!.sleeves[selectedSleeve]!.statistics.average_sp_rating
      : accountDetails.statistics.average_sp_rating
    const avgMoodysRating = selectedSleeve
      ? // biome-ignore lint/style/noNonNullAssertion: Bulk suppression as part of biome migration
        accountDetails!.sleeves[selectedSleeve]!.statistics
          .average_moodys_rating
      : accountDetails.statistics.average_moodys_rating
    const avgBookYtw = selectedSleeve
      ? // biome-ignore lint/style/noNonNullAssertion: suppress noUncheckedIndexedAccess
        accountDetails.sleeves[selectedSleeve]!.statistics.average_book_ytw
      : accountDetails.statistics.average_book_ytw

    // Get rating display based on permissions
    const ratingDisplay = getRatingDisplay(
      ratingsAccess,
      avgMoodysRating,
      avgSpRating,
    )

    // Compute comparison stats for deltas when provided
    const cmp = comparisonAccountDetails
      ? // biome-ignore lint/style/noNestedTernary: Existing code — enable rule without runtime changes
        selectedSleeve
        ? comparisonAccountDetails.sleeves[selectedSleeve]?.statistics
        : comparisonAccountDetails.statistics
      : undefined

    const cmpAvgYtw = cmp?.average_ytw ?? null
    const cmpAvgMaturity = cmp?.average_maturity ?? null
    const cmpAvgDuration = cmp?.average_duration ?? null

    const stats: Array<{
      label: string
      value: string | React.ReactNode
      delta?: number
      deltaFormatter?: (v: number) => string
    }> = [
      {
        label: 'Avg YTW',
        value: (
          <>
            <BodySubtext fontWeight={FontWeight.Medium}>
              {formatYield(avgYtw)}
            </BodySubtext>
            {avgPurchaseYtw !== undefined && (
              <BodyXS className="text-secondary">
                ({formatYield(avgPurchaseYtw)} purchase YTW)
              </BodyXS>
            )}
          </>
        ),
        delta:
          showDeltas && cmpAvgYtw !== null && cmpAvgYtw !== undefined
            ? avgYtw - (cmpAvgYtw as number)
            : undefined,
        deltaFormatter: (v: number) => formatPercentage(v, { numDigits: 3 }),
      },
    ]

    // Add Avg Book YTW right after Avg YTW if available
    if (avgBookYtw !== undefined && avgBookYtw !== null) {
      stats.push({
        label: 'Avg Book YTW',
        value: formatYield(avgBookYtw),
      })
    }

    stats.push(
      {
        label: 'Avg maturity',
        value: `${formatNumber(avgMaturity)} years`,
        delta:
          showDeltas && cmpAvgMaturity !== null && cmpAvgMaturity !== undefined
            ? avgMaturity - (cmpAvgMaturity as number)
            : undefined,
        deltaFormatter: (v: number) => `${formatNumber(v)}`,
      },
      {
        label: 'Avg duration',
        value: `${formatNumber(avgDuration)}`,
        delta:
          showDeltas && cmpAvgDuration !== null && cmpAvgDuration !== undefined
            ? avgDuration - (cmpAvgDuration as number)
            : undefined,
        deltaFormatter: (v: number) => `${formatNumber(v)}`,
      },
      {
        label: ratingDisplay.label,
        value: ratingDisplay.value,
      },
    )

    // Add Buy/Sell orders cards if we have proposal data
    if (showOrderStats && proposal && selectedAccount) {
      stats.push(
        {
          label: 'Buy orders',
          value: (
            <>
              <BodySubtext fontWeight={FontWeight.Medium}>
                {formatMoney(orderStats.buyOrders)}
              </BodySubtext>
              <BodyXS className="text-secondary">
                ({orderStats.buyOrdersCount}{' '}
                {orderStats.buyOrdersCount === 1 ? 'order' : 'orders'})
              </BodyXS>
            </>
          ),
        },
        {
          label: 'Sell orders',
          value: (
            <>
              <BodySubtext fontWeight={FontWeight.Medium}>
                {formatMoney(orderStats.sellOrders)}
              </BodySubtext>
              <BodyXS className="text-secondary">
                ({orderStats.sellOrdersCount}{' '}
                {orderStats.sellOrdersCount === 1 ? 'order' : 'orders'})
              </BodyXS>
            </>
          ),
        },
      )
    }

    return stats
  }, [
    accountDetails,
    selectedSleeve,
    comparisonAccountDetails,
    showDeltas,
    ratingsAccess,
    showOrderStats,
    proposal,
    selectedAccount,
    orderStats,
  ])

  const holdings = useMemo(() => {
    if (!accountDetails) return []
    return accountDetails.lots
      .filter((lot: Lot) => {
        if (selectedSleeve) {
          return lot.sleeve_id === selectedSleeve
        }
        return true
      })
      .map((lot: Lot) => ({
        isin: lot.instrument.isin,
        quantity: lot.open_quantity,
      }))
  }, [accountDetails, selectedSleeve])

  const { cashflowData, isLoading: isLoadingCashflowData } = useCashflowData({
    holdings,
  })

  // Not all accounts have cashflow targets
  const cashflowTargets = useMemo(() => {
    try {
      return (
        // biome-ignore lint/style/noNonNullAssertion: Bulk suppression as part of biome migration
        accountDetails!.sleeves[selectedSleeve!]!.model_assignment
          ?.cashflow_targets || []
      )
    } catch {
      return []
    }
  }, [accountDetails, selectedSleeve])

  return (
    <>
      <AccountSummaryStats>
        {summaryStats.map((stat) =>
          showDeltas && stat.delta !== undefined ? (
            <StatCardWithDelta
              key={stat.label}
              label={stat.label}
              value={stat.value as string | number}
              delta={stat.delta}
              deltaFormatter={stat.deltaFormatter}
              isLoading={isLoadingAccountDetails}
              skeletonWidth="50%"
            />
          ) : (
            <StatCard
              key={stat.label}
              label={stat.label}
              value={stat.value}
              loadingState={
                isLoadingAccountDetails
                  ? {
                      isLoading: true,
                      animationType: LoadingAnimationType.SKELETON,
                      skeletonWidth: '50%',
                    }
                  : undefined
              }
            />
          ),
        )}
      </AccountSummaryStats>

      <div className={`mt-[24px] ${chartContainerClassName || ''}`}>
        {cashflowTargets.length > 0 && !isLoadingCashflowData ? (
          <CashflowIncomeChart
            incomeData={cashflowData.all_cashflows}
            cashflowTargets={cashflowTargets}
          />
        ) : (
          <AccountInvestmentIncomeBarChart
            account={accountDetails}
            selectedSleeve={selectedSleeve}
            title="Investment income"
            isLoadingAccountDetails={isLoadingAccountDetails}
          />
        )}
      </div>

      <Distributions
        distributionData={distributionData}
        total={totalForDistribution}
        assetClassSet={assetClasses}
        showHeader={showDistributionHeader}
        gridClassName={distributionGridClassName}
      />
    </>
  )
}
